import { Injectable, signal } from '@angular/core';

export type ConsentChoice = 'granted' | 'denied';

/**
 * Where the visitor's cookie choice is kept. index.html reads the same key
 * before gtag.js loads, so a returning visitor's choice applies to the first
 * page view too. Change both places together.
 */
export const CONSENT_STORAGE_KEY = 'ezatwork.consent.v1';

interface StoredConsent {
  choice: ConsentChoice;
  at: string;
}

/**
 * The visitor's answer to the cookie banner, applied to Google Consent Mode v2.
 *
 * Defaults are set in index.html: denied in the EEA, the UK and Switzerland,
 * granted elsewhere. ad_personalization stays denied everywhere: we measure
 * sign-ups from ads, we don't build remarketing audiences.
 */
@Injectable({ providedIn: 'root' })
export class ConsentService {
  /** null until the visitor answers the banner. */
  readonly choice = signal<ConsentChoice | null>(this.read());

  accept(): void {
    this.save('granted');
  }

  decline(): void {
    this.save('denied');
  }

  /** Forget the answer so the banner asks again (from the cookie policy page). */
  reset(): void {
    try {
      localStorage.removeItem(CONSENT_STORAGE_KEY);
    } catch { /* storage blocked: the banner still shows for this visit */ }
    this.choice.set(null);
  }

  private save(choice: ConsentChoice): void {
    const stored: StoredConsent = { choice, at: new Date().toISOString() };
    try {
      localStorage.setItem(CONSENT_STORAGE_KEY, JSON.stringify(stored));
    } catch { /* storage blocked: the choice holds for this visit only */ }
    this.choice.set(choice);
    this.applyToGtag(choice);
  }

  private applyToGtag(choice: ConsentChoice): void {
    try {
      const gtag = (window as any).gtag;
      if (typeof gtag === 'function') {
        gtag('consent', 'update', {
          analytics_storage: choice,
          ad_storage: choice,
          ad_user_data: choice,
          ad_personalization: 'denied',
        });
      }
    } catch { /* analytics must never break the page */ }
  }

  private read(): ConsentChoice | null {
    try {
      const raw = localStorage.getItem(CONSENT_STORAGE_KEY);
      if (!raw) return null;
      const parsed = JSON.parse(raw) as Partial<StoredConsent>;
      return parsed.choice === 'granted' || parsed.choice === 'denied' ? parsed.choice : null;
    } catch {
      return null;
    }
  }
}
